import { useTranslation } from 'react-i18next';
import useFormHook from '@shared/hooks/useFormHook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { locations } from "@modules/organizationalStructure/locations/services/locations"
import { positionsService } from "@modules/organizationalStructure/positions/services/positions"
import { nomenclaturesOz } from "@modules/organizationalStructure/nomenclatures/services/nomenclaturesOz"
import { companiesService } from "@shared/services/companiesService"
import { structuralGroupService } from "@modules/organizationalStructure/structuralGroups/services/structuralGroups"
import { jobDescriptionsService } from "@modules/organizationalStructure/jobDescriptions/services/jobDescriptions"
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEffect, useState, useCallback } from 'react';
import LoadingButton from '@shared/components/LoadingButton';
import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function StructuralGroupsPositions({ onClose, onConfirm, editData, companyId, hrOrgUnitId, onSuccess, modalType }) {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();


    // Get selectedRecord from Redux
    const { selectedRecord, selectedCompany } = useSelector(state => state.structuralGroup);


    const [locationsData, setLocationsData] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [positionsData, setPositionsData] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(true);
    const [managerLevelsData, setManagerLevelsData] = useState([]);
    const [managerLevelsLoading, setManagerLevelsLoading] = useState(true);
    const [companyCentersData, setCompanyCentersData] = useState([]);
    const [companyCentersLoading, setCompanyCentersLoading] = useState(true);
    const [positionCostCenterData, setPositionCostCenterData] = useState(null);
    const [positionCostCenterLoading, setPositionCostCenterLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchedPositionData, setFetchedPositionData] = useState(null);
    const [positionDataLoading, setPositionDataLoading] = useState(false);
    const [jobDescriptionsData, setJobDescriptionsData] = useState([]);
    const [jobDescriptionsLoading, setJobDescriptionsLoading] = useState(false);
    const defaultCompanyId = companyId || 1;
    const managerLevels = 3;

    const validationSchema = yup.object().shape({
        locationId: yup.string().required(t('common.required')),
        city: yup.string().required(t('common.required')),
        hrJobId: yup.string().required(t('common.required')),
        costCenterId: yup.string().required(t('common.required')),
        positionNumber: yup.string().required(t('common.required')),
        hoursOfWork: yup.string().required(t('common.required')),
        orderPriority: yup.string().required(t('common.required')),
        status: yup.string().required(t('common.required')),
        numberOfPositions: yup.string().required(t('common.required')),
       

    });

    const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            openDate: new Date(),
            ftePercent: 100
        }
    });


    const watchedLocationId = watch('locationId');
    const watchedPositionId = watch('hrJobId');
    const watchedCostCenterId = watch('costCenterId');
    const watchedManagerLevelId = watch('managementLevelId');
    const watchedStatus = watch('status');
    const watchedCity = watch('city');
    const watchedHrJobDescriptionId = watch('hrJobDescriptionId');

    // Helper function to check if closeDate is the future date placeholder and convert to null for display
    const normalizeCloseDateForDisplay = (closeDate) => {
        if (!closeDate) return null;
        // Check if it's the future date placeholder in various formats
        if (closeDate === '2099-12-31' || 
            closeDate === '2099-12-31T00:00:00.000Z' ||
            closeDate === '2099-12-31T00:00:00Z' ||
            (typeof closeDate === 'string' && closeDate.startsWith('2099-12-31'))) {
            return null;
        }
        return closeDate;
    };

    const handleClose = () => {
        reset();
        setPositionCostCenterData(null);
        onClose();
    };


    const submitForm = async (data) => {
        setLoading(true);
        try {
            // If closeDate is empty, set it to a future date (2099-12-31) for server
            const closeDate = data.closeDate || '2099-12-31';
            // If ftePercent is empty, set it to 100
            const ftePercent = data.ftePercent || 100;
            
            const transformedData = {
                ...data,
                status: data.status === "1" ? "P" : "T",
                hrOrgUnitId: hrOrgUnitId,
                managementLevelId: data.managementLevelId ? parseInt(data.managementLevelId) : null,
                closeDate: closeDate,
                ftePercent: ftePercent
            };
            await onConfirm(transformedData);

            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (error) {
            notifyError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (modalType === "add") {
            reset({
                openDate: new Date(),
                ftePercent: 100
            });
        }
    }, [modalType, reset]);

    // Fetch position data from API when editing
    useEffect(() => {
        const fetchPositionData = async () => {
            const positionId = editData?.id || editData?.originalData?.id;

            if (!positionId || modalType === "add") {
                setFetchedPositionData(null);
                return;
            }

            try {
                setPositionDataLoading(true);
                const response = await structuralGroupService.getStructuralGroupPositionById(positionId);
                setFetchedPositionData(response);
            } catch (error) {
                console.error('❌ Error fetching position data:', error);
                setFetchedPositionData(null);
            } finally {
                setPositionDataLoading(false);
            }
        };

        fetchPositionData();
    }, [editData?.id, editData?.originalData?.id, modalType]);

    useEffect(() => {
        // Use fetched data if available, otherwise fall back to editData
        if (fetchedPositionData || editData) {
            const positionData = fetchedPositionData || editData.originalData || editData;

            const locationId = positionData.locationId || positionData.location?.id;
            const costCenterId = positionData.costCenterId || positionData.costCenter?.id || positionData.empOfficeId;
            // Priority: direct managementLevelId, then nested managementLevel.id, then fallbacks
            const managementLevelId = positionData.managementLevelId || positionData.managementLevel?.id || positionData.structuralGroupId || positionData.hrPositionLevelId || positionData.hrOrgUnitLevelsId;

            if (!setValue || !watch) {
                return;
            }

            setTimeout(() => {
                const setFormValues = () => {
                    setValue('locationId', locationId);
                    setValue('costCenterId', costCenterId);
                    setValue('managementLevelId', managementLevelId);
                    setValue('positionNumber', positionData.positionNumber);
                    setValue('hoursOfWork', positionData.hoursOfWork);
                    setValue('ftePercent', positionData.ftePercent);
                    setValue('addPayedLeave', positionData.addPayedLeave);
                    setValue('orderPriority', positionData.orderPriority);
                    setValue('status', positionData.status === 'P' ? '1' : '2');
                    setValue('numberOfPositions', positionData.positionNumber);
                    setValue('budget', positionData.budget);
                    setValue('openDate', positionData.openDate);
                    // If closeDate is the future date (2099-12-31), show it as empty to user
                    setValue('closeDate', normalizeCloseDateForDisplay(positionData.closeDate));
                    setValue('hrJobId', positionData.hrJob?.id || positionData.hrJobId);
                    setValue('hrJobDescriptionId', positionData.hrJobDescriptionId || positionData.hrJobDescription?.id);

                    if (positionData.location?.cityName) {
                        setValue('city', positionData.location.cityName);
                    }
                };

                setFormValues();
            }, 50);
        } else {
            reset();
        }
    }, [fetchedPositionData, editData?.id]);

    useEffect(() => {
        if (selectedRecord?.empOffices && setValue && modalType !== "add") {
            const costCenterId = selectedRecord.empOffices.officeId || selectedRecord.empOffices.officeNo;
            if (costCenterId) {
                setValue('costCenterId', costCenterId);
            }
        }
    }, [selectedRecord?.empOffices, setValue, modalType]);

    useEffect(() => {
        if (selectedRecord?.hrPositions?.length > 0 && setValue && !editData && modalType !== "add") {
            const firstPosition = selectedRecord.hrPositions[0];

            const locationId = firstPosition.location?.id;
            const hrJobId = firstPosition.hrJob?.id;
            const structuralGroupId = firstPosition.hrOrgUnit?.id;

            if (locationId) setValue('locationId', locationId);
            if (hrJobId) setValue('hrJobId', hrJobId);

            if (firstPosition.positionNumber) setValue('positionNumber', firstPosition.positionNumber);
            if (firstPosition.hoursOfWork) setValue('hoursOfWork', firstPosition.hoursOfWork);
            if (firstPosition.ftePercent) setValue('ftePercent', firstPosition.ftePercent);
            if (firstPosition.addPayedLeave !== undefined) setValue('addPayedLeave', firstPosition.addPayedLeave);
            if (firstPosition.orderPriority) setValue('orderPriority', firstPosition.orderPriority);
            if (firstPosition.status) setValue('status', firstPosition.status === 'P' ? '1' : '2');
            if (firstPosition.numberOfPositions) setValue('numberOfPositions', firstPosition.numberOfPositions);
            if (firstPosition.budget) setValue('budget', firstPosition.budget);
            if (firstPosition.openDate) setValue('openDate', firstPosition.openDate);
            if (firstPosition.closeDate) setValue('closeDate', normalizeCloseDateForDisplay(firstPosition.closeDate));

        }
    }, [selectedRecord?.hrPositions, setValue, editData, modalType]);

    useEffect(() => {
        if (selectedRecord?.hrPositions?.length > 0 && setValue && modalType !== "add") {
            const firstPosition = selectedRecord.hrPositions[0];

            const locationName = firstPosition.location?.name;
            const hrJobCode = firstPosition.hrJob?.code;
            const structuralGroupId = firstPosition.hrOrgUnit?.id;

            if (firstPosition.positionNumber) setValue('positionNumber', firstPosition.positionNumber);
            if (firstPosition.hoursOfWork) setValue('hoursOfWork', firstPosition.hoursOfWork);
            if (firstPosition.ftePercent) setValue('ftePercent', firstPosition.ftePercent);
            if (firstPosition.addPayedLeave !== undefined) setValue('addPayedLeave', firstPosition.addPayedLeave);
            if (firstPosition.orderPriority) setValue('orderPriority', firstPosition.orderPriority);
            if (firstPosition.status) setValue('status', firstPosition.status === 'P' ? '1' : '2');
            if (firstPosition.numberOfPositions) setValue('numberOfPositions', firstPosition.numberOfPositions);
            if (firstPosition.budget) setValue('budget', firstPosition.budget);
            if (firstPosition.openDate) setValue('openDate', firstPosition.openDate);
            if (firstPosition.closeDate) setValue('closeDate', normalizeCloseDateForDisplay(firstPosition.closeDate));
        }
    }, [selectedRecord?.hrPositions, setValue, modalType]);

    useEffect(() => {
        const positionData = fetchedPositionData || editData?.originalData;

        if (positionData && setValue && locationsData.length > 0 && positionsData.length > 0) {
            const locationName = positionData.location?.name;
            if (locationName && locationsData.length > 0) {
                const matchedLocation = locationsData.find(loc => loc.name === locationName);
                if (matchedLocation) {
                    setValue('locationId', matchedLocation.id);
                }
            }

            const hrJobDescription = positionData.hrJob?.longDescription;

            if (hrJobDescription && positionsData.length > 0) {
                const matchedPosition = positionsData.find(pos => pos.longDescription === hrJobDescription);
                if (matchedPosition) {
                    setValue('hrJobId', matchedPosition.id);
                } else {
                    console.error('❌ No hrJob found with description:', hrJobDescription);
                }
            }
        }
    }, [fetchedPositionData, editData?.originalData, setValue, locationsData, positionsData]);

    useEffect(() => {
        const positionData = fetchedPositionData || editData?.originalData;

        if (positionData && setValue && companyCentersData.length > 0 && managerLevelsData.length > 0) {
            // Try multiple ways to find cost center
            let costCenterId = null;

            // Method 1: From selectedRecord.empOffices
            if (selectedRecord?.empOffices?.name) {
                const costCenterName = selectedRecord.empOffices.name;
                const matchedCostCenter = companyCentersData.find(center => center.name === costCenterName);
                if (matchedCostCenter) {
                    costCenterId = matchedCostCenter.officeId;
                }
            }

            // Method 2: From positionData directly
            if (!costCenterId && positionData.costCenterId) {
                costCenterId = positionData.costCenterId;
            }

            // Method 3: From hrOrgUnit (structural group might have cost center info)
            if (!costCenterId && positionData.hrOrgUnit?.empOfficeId) {
                costCenterId = positionData.hrOrgUnit.empOfficeId;
            }

            // Only set cost center if we haven't already set it from the API call
            if (costCenterId && !positionCostCenterData) {
                setValue('costCenterId', costCenterId);
            } else if (!costCenterId && !positionCostCenterData) {
                console.error('❌ No cost center found through any method');
            }

            // Try to find manager level by name from managementLevel field
            if (positionData.managementLevel?.id && managerLevelsData.length > 0) {
                const matchedManagerLevel = managerLevelsData.find(level => level.id === positionData.managementLevel.id);
                if (matchedManagerLevel) {
                    setValue('managementLevelId', matchedManagerLevel.id);
                } else {
                    console.error('No manager level found with id:', positionData.managementLevel.id);
                }
            } else {
                // Fallback: Try to find manager level by id from hrOrgUnit
                const managerLevelId = positionData.hrOrgUnit?.id;
                if (managerLevelId && managerLevelsData.length > 0) {
                    const matchedManagerLevel = managerLevelsData.find(level => level.id === managerLevelId);
                    if (matchedManagerLevel) {
                        setValue('managementLevelId', managerLevelId);
                    } else {
                        console.error('No manager level id found or manager levels data not loaded');
                    }
                }
            }
        }
    }, [fetchedPositionData, editData?.originalData, selectedRecord?.empOffices, setValue, companyCentersData, managerLevelsData, positionCostCenterData]);

    useEffect(() => {
        const positionData = fetchedPositionData || editData?.originalData;

        if (positionData && setValue) {
            if (positionData.positionNumber) {
                setValue('numberOfPositions', positionData.positionNumber);
            }
        }
    }, [fetchedPositionData, editData?.originalData, setValue]);

    useEffect(() => {
        if (selectedRecord?.hrPositions?.length > 0 && setValue && positionsData.length > 0 && modalType !== "add") {
            const firstPosition = selectedRecord.hrPositions[0];
            const hrJobName = firstPosition.hrJob?.longDescription;

            if (hrJobName && positionsData.length > 0) {
                const matchedPosition = positionsData.find(pos => pos.longDescription === hrJobName);
                if (matchedPosition) {
                    setValue('hrJobId', matchedPosition.id);
                } else {
                    console.error('No hrJob name found or positions data not loaded');
                }
            } else {
            }
        }
    }, [selectedRecord?.hrPositions, setValue, positionsData, modalType]);

    useEffect(() => {
        if (selectedRecord?.hrPositions?.length > 0 && setValue && managerLevelsData.length > 0 && modalType !== "add") {
        }
    }, [selectedRecord?.hrPositions, setValue, managerLevelsData, modalType]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setLocationsLoading(true);
                const response = await locations.getLocations();
                const locationsArray = Array.isArray(response) ? response : (response.content || response.data || []);
                setLocationsData(Array.isArray(locationsArray) ? locationsArray : []);
            } catch (error) {
                console.error('Error fetching locations:', error);
                setLocationsData([]);
            } finally {
                setLocationsLoading(false);
            }
        };

        const fetchPositions = async () => {
            const companyIdToUse = selectedCompany || defaultCompanyId;
          
            try {
                setPositionsLoading(true);
                const response = await positionsService.getAllPositions({
                    id: companyIdToUse,
                });
                const positionsArray = Array.isArray(response) ? response : (response.content || response.data || []);
                const finalPositions = Array.isArray(positionsArray) ? positionsArray : [];
                setPositionsData(finalPositions);
            } catch (error) {
                console.error('Error fetching positions:', error);
                setPositionsData([]);
            } finally {
                setPositionsLoading(false);
            }
        };

        const fetchManagerLevels = async () => {
            try {
                setManagerLevelsLoading(true);
                const response = await nomenclaturesOz.getNomenclatureItems(managerLevels, "true");
                setManagerLevelsData(response);
            } catch (error) {
                console.error('Error fetching manager levels:', error);
                setManagerLevelsData([]);
            } finally {
                setManagerLevelsLoading(false);
            }
        };

        const fetchCompanyCenters = async () => {

            try {
                setCompanyCentersLoading(true);
                const response = await companiesService.getCompanyCenters();
                setCompanyCentersData(response);
            } catch (error) {
                console.error('Error fetching company centers:', error);
                setCompanyCentersLoading(false);
            } finally {
                setCompanyCentersLoading(false);
            }
        };

        const fetchJobDescriptions = async () => {
            const companyIdToUse = selectedCompany || defaultCompanyId;

            try {
                setJobDescriptionsLoading(true);
                const response = await jobDescriptionsService.getJobDescriptions(companyIdToUse);
                const jobDescriptionsArray = Array.isArray(response) ? response : (response.content || response.data || []);
                setJobDescriptionsData(Array.isArray(jobDescriptionsArray) ? jobDescriptionsArray : []);
            } catch (error) {
                console.error('Error fetching job descriptions:', error);
                setJobDescriptionsData([]);
            } finally {
                setJobDescriptionsLoading(false);
            }
        };

        fetchLocations();
        fetchPositions();
        fetchManagerLevels();
        fetchCompanyCenters();
        fetchJobDescriptions();
    }, [selectedRecord, selectedCompany, companyId]);

    useEffect(() => {
        if (watchedLocationId && locationsData.length > 0) {
            const selectedLocation = locationsData.find(location => location.id === parseInt(watchedLocationId));
            if (selectedLocation && selectedLocation.cityName) {
                setValue('city', selectedLocation.cityName);
            }
        } else if (!watchedLocationId) {
            setValue('city', '');
        }
    }, [watchedLocationId, locationsData, setValue]);

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" style={{ maxHeight: '90%', maxWidth: '1400px', width: '85%' }} ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.editOf') : t('common.add')} {t('organizationalStructure.structuralGroups.positionsForm.positionsForm')}
                            {positionDataLoading && <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>Loading...</span>}
                        </h4>
                        <h5 className="modal-subtitle">
                            {t('organizationalStructure.structuralGroups.positionsForm.structuralGroup')}: {selectedRecord?.longDescription || 'N/A'}
                        </h5>
                        <form id="structuralGroupsPositionsForm" className="region-form" onSubmit={handleSubmit(submitForm)} ref={modalRef}>
                            <div className="flex-column">
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.location')}<span className="required">*</span></span>
                                    <select
                                        className={`small-select ${errors.locationId ? 'input-error' : ''}`}
                                        value={watchedLocationId || ''}
                                        {...register('locationId')}
                                        onChange={(e) => {
                                            setValue('locationId', e.target.value);
                                        }}
                                    >
                                        <option value="">{t('common.select')}</option>
                                    {locationsLoading ? (
                                        <option value="">Loading locations...</option>
                                    ) : (!Array.isArray(locationsData) || locationsData.length === 0) ? (
                                        <option value="">No locations found</option>
                                    ) : (
                                        locationsData.map((location) => (
                                            <option key={location.id} value={location.id}>{location.name}</option>
                                        ))
                                    )}
                                </select>
                                <span className="span-bold no-wrap">{t('organizationalStructure.structuralGroups.positionsForm.positionNumber')}<span className="required">*</span></span>
                                <input type="text" maxLength={10} className={`small-input ${errors.positionNumber ? 'input-error' : ''}`} placeholder={errors.positionNumber ? errors.positionNumber.message : t('common.required')} {...register('positionNumber')} />
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.city')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`small-input ${errors.city ? 'input-error' : ''}`}
                                    value={watchedCity || ''}
                                    {...register('city')}
                                    disabled
                                    placeholder="Автоматично попълва се при избор на локация"
                                />
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.position')}<span className="required">*</span></span>
                                <select
                                    className={`small-select ${errors.hrJobId ? 'input-error' : ''}`}
                                    value={watchedPositionId || ''}
                                    {...register('hrJobId')}
                                    onChange={(e) => {
                                        setValue('hrJobId', e.target.value);
                                    }}
                                    disabled={!!editData}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {positionsLoading ? (
                                        <option value="">Loading positions...</option>
                                    ) : (!Array.isArray(positionsData) || positionsData.length === 0) ? (
                                        <option value="">No positions found</option>
                                    ) : (
                                        positionsData.map((position) => (
                                            <option key={position.id} value={position.id}>
                                                {position.jobCode} - {position.longDescription}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.costCenter')}<span className="required">*</span></span>
                                <select
                                    className={`small-select ${errors.costCenterId ? 'input-error' : ''}`}
                                    value={watchedCostCenterId || ''}
                                    {...register('costCenterId')}
                                    onChange={(e) => {
                                        setValue('costCenterId', e.target.value);
                                    }}
                                    disabled={!!editData}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {companyCentersLoading ? (
                                        <option value="">Loading company centers...</option>
                                    ) : (!Array.isArray(companyCentersData) || companyCentersData.length === 0) ? (
                                        <option value="">No company centers found</option>
                                    ) : (
                                        companyCentersData.map((companyCenter) => (
                                            <option key={companyCenter.officeId} value={companyCenter.officeId}>
                                                {companyCenter.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.managerLevel')}</span>
                                <select
                                    className="small-select"
                                    value={watchedManagerLevelId || ''}
                                    {...register('managementLevelId')}
                                    onChange={(e) => {
                                        setValue('managementLevelId', e.target.value);
                                    }}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {managerLevelsLoading ? (
                                        <option value="">Loading manager levels...</option>
                                    ) : (!Array.isArray(managerLevelsData) || managerLevelsData.length === 0) ? (
                                        <option value="">No manager levels found</option>
                                    ) : (
                                        managerLevelsData.map((managerLevel) => (
                                            <option key={managerLevel.id} value={managerLevel.id}>
                                                {managerLevel.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <div className="flex-row width-50">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.hoursOfWork')}<span className="required">*</span></span>
                                    <input type="number" maxLength={2}
                                        onInput={(e) => {
                                            if (e.target.value.length > 2) {
                                                e.target.value = e.target.value.slice(0, 2);
                                            }
                                        }}
                                        className={`small-input ${errors.hoursOfWork ? 'input-error' : ''}`} placeholder={errors.hoursOfWork ? errors.hoursOfWork.message : t('common.required')} {...register('hoursOfWork')} />
                                </div>
                                <div className="flex-row width-50">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.ftePercent')}</span>
                                    <input type="number"
                                        maxLength={6}
                                        onInput={(e) => {
                                            if (e.target.value.length > 6) {
                                                e.target.value = e.target.value.slice(0, 6);
                                            }
                                        }}
                                        className={`small-input ${errors.ftePercent ? 'input-error' : ''}`}{...register('ftePercent')} />
                                </div>
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <div className="flex-row width-50">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.addPayedLeave')}</span>
                                    <input type="number"
                                        maxLength={2}
                                        onInput={(e) => {
                                            if (e.target.value.length > 2) {
                                                e.target.value = e.target.value.slice(0, 2);
                                            }
                                        }}
                                        className="small-input" {...register('addPayedLeave')} />
                                </div>
                                <div className="flex-row width-50">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.priority')}<span className="required">*</span></span>
                                    <input type="number"
                                        maxLength={20}
                                        onInput={(e) => {
                                            if (e.target.value.length > 20) {
                                                e.target.value = e.target.value.slice(0, 20);
                                            }
                                        }}
                                        className={`small-input ${errors.orderPriority ? 'input-error' : ''}`} placeholder={errors.orderPriority ? errors.orderPriority.message : t('common.required')} {...register('orderPriority')} />
                                </div>
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.status')}<span className="required">*</span></span>
                                <select
                                    className="small-select"
                                    value={watchedStatus || ''}
                                    {...register('status')}
                                    onChange={(e) => {
                                        setValue('status', e.target.value);
                                    }}
                                >
                                    <option value="1">{t('organizationalStructure.structuralGroups.positionsForm.permanent')}</option>
                                    <option value="2">{t('organizationalStructure.structuralGroups.positionsForm.temporary')}</option>
                                </select>
                                {errors.status && <p className="error-message compact">{errors.status.message}</p>}
                            </div>
                            {!editData && (
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.count')}<span className="required">*</span></span>
                                    <input type="number" placeholder={errors.numberOfPositions ? errors.numberOfPositions.message : t('common.required')} className={`small-input ${errors.numberOfPositions ? 'input-error' : ''}`} {...register('numberOfPositions')} />
                                </div>
                            )}
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.budget')}</span>
                                <input type="number"
                                    maxLength={20}
                                    onInput={(e) => {
                                        if (e.target.value.length > 20) {
                                            e.target.value = e.target.value.slice(0, 20);
                                        }
                                    }}
                                    className="small-input" {...register('budget')} />
                            </div>
                            <div className="flex-row margin-bottom-5">
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.positionsForm.openDate')}</span>
                                <CustomDatePicker
                                    name="openDate"
                                    value={watch('openDate')}
                                    onChange={(date) => {
                                        setValue('openDate', date);
                                    }}
                                    isSmall={true}
                                    disabled={!!editData}
                                />
                                <span className="span-bold no-wrap">{t('organizationalStructure.structuralGroups.positionsForm.closeDate')}</span>
                                <CustomDatePicker
                                    name="closeDate"
                                    value={watch('closeDate')}
                                    minDate={watch('openDate') || new Date()}
                                    onChange={(date) => {
                                        setValue('closeDate', date);
                                    }}
                                    isSmall={true}
                                />
                            </div>
                            <h4 className="modal-subtitle text-center">{t('organizationalStructure.positions.form.fileUpload')}</h4>
                            <div className="flex-row margin-bottom-5" style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '10px', borderColor: '#ccc' }}>
                                <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.positions.form.file')}</span>
                                <select
                                    className="small-input"
                                    value={watchedHrJobDescriptionId || ''}
                                    {...register('hrJobDescriptionId')}
                                    disabled={jobDescriptionsLoading}
                                >
                                    <option value="">{jobDescriptionsLoading ? 'Зареждане...' : 'Изберете'}</option>
                                    {jobDescriptionsData.map((jobDesc, index) => (
                                        <option key={`file-${jobDesc.id}-${index}`} value={jobDesc.id}>
                                            {jobDesc.jobDescriptionName || jobDesc.fileName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                    </div>
                </form>

            </div>
            <div className="modal-footer">
                <LoadingButton type="submit" form="structuralGroupsPositionsForm" className="btn btn-primary" loading={loading} disabled={loading}>{t('common.save')}</LoadingButton>
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>{t('common.cancel')}</button>
            </div>
        </div>
            </div >
        </div >
    );
}